import type { ScriptMismatchDetail, ScriptOkDetail, ScriptCheckedDetail } from "../types";
import { sha256 } from "./crypto";
import { emit } from "./events";
import { normalizeInline, isVolatileInline } from "./inline";
import { RuntimeManifest, urlMatchesPin } from "./manifest";

function emitCheckedVerbose(enabled: boolean, payload: ScriptCheckedDetail) {
  if (!enabled) return;
  window.dispatchEvent(
    new CustomEvent("jsentinel", {
      detail: { type: "script.checked", data: payload } as any,
      bubbles: true,
      composed: true,
    }),
  );
}

/* ---------- Helpers for snippet fallback ---------- */

// returns true for non-executable data scripts (we don't hash these)
function isDataScriptType(t: string | null | undefined) {
  const type = (t || "").trim().toLowerCase();
  return type === "application/json" || type === "application/ld+json";
}

// stable normalized type name
function normType(t: string | null | undefined) {
  return (t || "text/javascript").trim().toLowerCase();
}

// build a safe snippet payload (first N chars, byte size, tiny checksum)
function buildSnippetPayload(text: string, limit = 512) {
  const snippet = text.slice(0, limit);
  const truncated = text.length > limit;
  let cksum = 0;
  for (let i = 0; i < snippet.length; i++) cksum = (cksum + snippet.charCodeAt(i)) >>> 0;
  return {
    snippet,
    truncated,
    bytes: new TextEncoder().encode(text).length,
    snippetChecksum: `xx32-${cksum.toString(16)}`,
  };
}

/* -------------------------------------------------- */

export async function check(
  node: HTMLScriptElement,
  man: RuntimeManifest,
  verbose: boolean,
  relaxedHosts: string[] = [],
) {
  try {
    if (!node.src) {
      const raw = node.textContent || "";
      const type = normType(node.type);

      // 1) Data scripts (JSON/LD-JSON): skip hashing, emit snippet-based notice/warn
      if (isDataScriptType(type)) {
        const sn = buildSnippetPayload(raw, 512);

        emitCheckedVerbose(verbose, {
          key: "inline",
          src: null,
          inline: true,
          expected: undefined,
          got: "",
          ok: true, // integrity açısından dışarıda tutuyoruz
        });

        // mismatch benzeri ama snippet içeriyor; severity'yi "warn" tutuyoruz
        const data = {
          key: "inline",
          src: null,
          inline: true,
          expected: undefined,
          got: "",
          ok: false,
          severity: "warn",
          reason: "no-hash-snippet",
          meta: {
            id: node.id || undefined,
            type,
            ...sn,
          },
        } as ScriptMismatchDetail; // meta eklediğimiz için genişletiyoruz
        emit({ type: "script.mismatch", data });
        return false;
      }

      // 2) Executable inline: try to hash; on failure fall back to snippet
      let got = "";
      let hashed = false;
      try {
        const norm = normalizeInline(raw);
        got = await sha256(norm);
        hashed = true;
      } catch {
        hashed = false;
      }

      if (hashed) {
        const ok = man.inlineHashes.has(got);

        emitCheckedVerbose(verbose, {
          key: "inline",
          src: null,
          inline: true,
          expected: ok ? got : undefined,
          got,
          ok,
        });

        if (ok) {
          const data: ScriptOkDetail = {
            key: "inline",
            src: null,
            inline: true,
            expected: got,
            got,
            ok: true,
            severity: "info",
          };
          emit({ type: "script.ok", data });
          return true;
        }

        const volatile = isVolatileInline(node, raw);
        const data: ScriptMismatchDetail = {
          key: "inline",
          src: null,
          inline: true,
          expected: undefined,
          got,
          ok: false,
          severity: volatile ? "warn" : "error",
          reason: volatile ? "volatile-inline" : "manifest-missing-key",
        };
        emit({ type: "script.mismatch", data });
        return false;
      } else {
        // hashing failed -> provide snippet
        const sn = buildSnippetPayload(raw, 512);

        emitCheckedVerbose(verbose, {
          key: "inline",
          src: null,
          inline: true,
          expected: undefined,
          got: "",
          ok: false,
        });

        const data = {
          key: "inline",
          src: null,
          inline: true,
          expected: undefined,
          got: "",
          ok: false,
          severity: "warn",
          reason: "no-hash-snippet",
          meta: {
            id: node.id || undefined,
            type,
            ...sn,
          },
        } as any;
        emit({ type: "script.mismatch", data });
        return false;
      }
    }

    const src = node.src;
    let sameOrigin = false;
    let pathname = "";
    let host = "";
    try {
      const u = new URL(src, window.location.href);
      sameOrigin = u.origin === window.location.origin;
      pathname = u.pathname;
      host = u.host;
    } catch {}

    if (sameOrigin) {
      const expectedDirect =
        man.sameOriginHashes.get(pathname) ||
        man.sameOriginHashes.get(decodeURIComponent(pathname)) ||
        man.sameOriginHashes.get(src);

      let expected = expectedDirect;
      if (!expected) {
        const fn = (pathname.split("/").pop() || "").toLowerCase();
        if (fn) {
          const byFile = man.filenameIndex.get(fn);
          if (byFile) expected = byFile;
        }
      }

      let got = "";
      let fetchFailed = false;
      try {
        const code = await (await fetch(src, { cache: "no-store" })).text();
        got = await sha256(code);
      } catch {
        fetchFailed = true;
      }

      if (fetchFailed) {
        const data: ScriptMismatchDetail = {
          key: pathname || src,
          src,
          inline: false,
          expected,
          got: "",
          ok: false,
          severity: "error",
          reason: "fetch-error",
        };
        emit({ type: "script.mismatch", data });
        return false;
      }

      const ok = expected === got;
      emitCheckedVerbose(verbose, {
        key: pathname || src,
        src,
        inline: false,
        expected,
        got,
        ok,
      });

      if (ok) {
        const data: ScriptOkDetail = {
          key: pathname || src,
          src,
          inline: false,
          expected,
          got,
          ok: true,
          severity: "info",
        };
        emit({ type: "script.ok", data });
        return true;
      } else {
        const data: ScriptMismatchDetail = {
          key: pathname || src,
          src,
          inline: false,
          expected,
          got,
          ok: false,
          severity: "error",
          reason: expected ? "hash-different" : "manifest-missing-key",
        };
        emit({ type: "script.mismatch", data });
        return false;
      }
    }

    if (!/^https:\/\//i.test(src)) {
      const data: ScriptMismatchDetail = {
        key: src,
        src,
        inline: false,
        expected: undefined,
        got: "",
        ok: false,
        severity: "warn",
        reason: "insecure-http",
      };
      emit({ type: "script.mismatch", data });
      return false;
    }

    let got = "";
    let fetched = false;
    try {
      const code = await (await fetch(src, { cache: "no-store" })).text();
      got = await sha256(code);
      fetched = true;
    } catch {}

    const versionedPinned = man.versionedPins.has(src);
    const dynamicPinned = urlMatchesPin(src, man.dynamicPins);
    const hostRelaxed = host && relaxedHosts.includes(host);

    if (versionedPinned || dynamicPinned || hostRelaxed) {
      emitCheckedVerbose(verbose, {
        key: src,
        src,
        inline: false,
        expected: hostRelaxed ? "(host-relaxed)" : "(pin)",
        got: fetched ? got : "",
        ok: true,
      });
      const data: ScriptOkDetail = {
        key: src,
        src,
        inline: false,
        expected: hostRelaxed ? "(host-relaxed)" : "(pin)",
        got: fetched ? got : "",
        ok: true,
        severity: "info",
      };
      emit({ type: "script.ok", data });
      return true;
    }

    const data: ScriptMismatchDetail = {
      key: src,
      src,
      inline: false,
      expected: undefined,
      got: fetched ? got : "",
      ok: false,
      severity: "warn",
      reason: "url-pin-mismatch",
    };
    emit({ type: "script.mismatch", data });
    return false;
  } catch (err: any) {
    emit({
      type: "error",
      data: { stage: "check", message: String(err?.message || err) },
    });
    return false;
  }
}
